import { z } from 'zod'

export const transportPreferenceSchema = z.enum(['car', 'moto', 'bike', 'walk'])
export const pacePreferenceSchema = z.enum(['relax', 'normal', 'intense'])
export const themePreferenceSchema = z.enum(['light', 'dark', 'auto'])
export const languagePreferenceSchema = z.enum(['fr', 'en', 'es'])
export const unitsPreferenceSchema = z.enum(['metric', 'imperial'])
export const mobileNavTypeSchema = z.enum(['standard', 'burger']).optional()
export const emailFrequencySchema = z.enum(['immediate', 'daily', 'weekly'])
export const twoFactorMethodSchema = z.enum(['app', 'sms']).nullable()

export const travelPreferencesSchema = z.object({
  defaultTransport: transportPreferenceSchema,
  defaultPace: pacePreferenceSchema,
  avoidTolls: z.boolean(),
  avoidHighways: z.boolean(),
  avoidFerries: z.boolean()
})

export const displayPreferencesSchema = z.object({
  theme: themePreferenceSchema,
  language: languagePreferenceSchema,
  units: unitsPreferenceSchema,
  mobileNavType: mobileNavTypeSchema,
  accentColor: z.string().optional()
})

export const notificationTypePreferencesSchema = z.object({
  tripReminders: z.boolean(),
  socialActivity: z.boolean(),
  achievements: z.boolean(),
  planUpdates: z.boolean()
})

export const notificationPreferencesSchema = z.object({
  push: z.boolean(),
  email: z.boolean(),
  emailFrequency: emailFrequencySchema,
  types: notificationTypePreferencesSchema
})

export const privacyPreferencesSchema = z.object({
  profilePublic: z.boolean(),
  showAvatar: z.boolean(),
  showBio: z.boolean(),
  showLocation: z.boolean(),
  showStats: z.boolean(),
  allowPublicTrips: z.boolean(),
  allowLocationTracking: z.boolean(),
  allowAnalytics: z.boolean(),
  shareLiveWithFriends: z.boolean(),
  shareLiveWithClans: z.boolean()
})

export const securityPreferencesSchema = z.object({
  twoFactorEnabled: z.boolean(),
  twoFactorMethod: twoFactorMethodSchema
})

export const userPreferencesSchema = z.object({
  userId: z.string().min(1),
  travel: travelPreferencesSchema,
  display: displayPreferencesSchema,
  notifications: notificationPreferencesSchema,
  privacy: privacyPreferencesSchema,
  security: securityPreferencesSchema,
  updatedAt: z.date().optional()
})

export const profileUpdateSchema = z.object({
  displayName: z
    .string()
    .min(2, 'Minimum 2 caractères')
    .max(50, 'Maximum 50 caractères')
    .transform(value => value.trim())
    .optional(),
  bio: z
    .string()
    .max(300, 'Maximum 300 caractères')
    .optional(),
  location: z
    .string()
    .max(100, 'Maximum 100 caractères')
    .optional()
    .nullable(),
  languages: z
    .array(z.string().min(1))
    .max(10)
    .optional(),
  interests: z
    .array(z.string().min(1))
    .max(20)
    .optional(),
  favorites: z.array(z.string()).optional(),
  avatarUrl: z.string().url().optional().nullable()
})

export const preferencesUpdateSchema = z.object({
  travel: travelPreferencesSchema.partial().optional(),
  display: displayPreferencesSchema.partial().optional(),
  notifications: notificationPreferencesSchema.partial().optional(),
  privacy: privacyPreferencesSchema.partial().optional(),
  security: securityPreferencesSchema.partial().optional()
}).refine(
  data =>
    data.travel ||
    data.display ||
    data.notifications ||
    data.privacy ||
    data.security,
  {
    message: 'At least one preference section must be provided'
  }
)

export const preferenceInteractionSchema = z.object({
  type: z.enum(['stop_visit', 'plan_create', 'plan_complete', 'rating', 'share']),
  category: z.string().min(1).max(50).optional(),
  duration: z.number().min(0).max(1440).optional(),
  rating: z.number().min(1).max(5).optional(),
  budget: z.enum(['low', 'medium', 'high']).optional(),
  company: z.enum(['solo', 'couple', 'family', 'friends']).optional(),
  timestamp: z.date().optional()
})
